import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Loader2,
  Search,
  MessageSquare,
  User,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Bug,
  Lightbulb,
  ChevronRight,
  RefreshCw,
  Filter,
  BookOpen,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const STATUS_CONFIG = {
  OPEN: { label: 'Open', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  RESOLVED: { label: 'Resolved', color: 'bg-lime-100 text-lime-700', icon: CheckCircle },
  CLOSED: { label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
};

const CATEGORY_CONFIG = {
  general: { label: 'General', icon: HelpCircle, color: 'text-slate-600' },
  account: { label: 'Account', icon: User, color: 'text-blue-600' },
  matching: { label: 'Matching', icon: AlertCircle, color: 'text-amber-600' },
  bug: { label: 'Bug Report', icon: Bug, color: 'text-red-600' },
  feedback: { label: 'Feature Request', icon: Lightbulb, color: 'text-purple-600' },
};

// Ticket Card Component
const TicketCard = ({ ticket, onClick }) => {
  const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
  const category = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.general;
  const StatusIcon = status.icon;
  const CategoryIcon = category.icon;

  return (
    <Card 
      className="hover:shadow-md transition-all cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon className={`h-5 w-5 ${category.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs font-mono">
                    {ticket.ticket_number}
                  </Badge>
                  <Badge className={status.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                </div>
                <h4 className="font-medium text-slate-900 group-hover:text-lime-700 transition-colors">
                  {ticket.subject}
                </h4>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-lime-500 transition-colors flex-shrink-0" />
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {ticket.user_name || ticket.user_email}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Ticket Detail Modal
const TicketDetailModal = ({ ticket, isOpen, onClose, onUpdate }) => {
  const [status, setStatus] = useState(ticket?.status || 'OPEN');
  const [adminNotes, setAdminNotes] = useState(ticket?.admin_notes || '');
  const [adminResponse, setAdminResponse] = useState(ticket?.admin_response || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setAdminNotes(ticket.admin_notes || '');
      setAdminResponse(ticket.admin_response || '');
    }
  }, [ticket]);

  if (!ticket) return null;

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
  const categoryConfig = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.general;
  const CategoryIcon = categoryConfig.icon;

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(ticket.id, { 
      status, 
      admin_notes: adminNotes,
      admin_response: adminResponse 
    });
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="font-mono">{ticket.ticket_number}</Badge>
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
          </div>
          <DialogTitle>{ticket.subject}</DialogTitle>
          <DialogDescription>
            Submitted {format(new Date(ticket.created_at), 'PPpp')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* User Info */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
              <User className="h-6 w-6 text-slate-500" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{ticket.user_name || 'No name provided'}</p>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                {ticket.user_email}
              </p>
            </div>
          </div>

          {/* Category & Message */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CategoryIcon className={`h-4 w-4 ${categoryConfig.color}`} />
              <span className="text-sm font-medium text-slate-700">{categoryConfig.label}</span>
            </div>
            <div className="p-4 bg-white border border-slate-200 rounded-lg">
              <p className="text-slate-700 whitespace-pre-wrap">{ticket.message}</p>
            </div>
          </div>

          {/* Response to User */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-lime-600" />
              Response to User
              {!ticket.admin_response && <Badge variant="outline" className="text-xs">New</Badge>}
            </label>
            <Textarea
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              placeholder="Write your response to the user. They will be notified and can see this in their ticket history."
              rows={5}
              className="border-lime-200 focus:border-lime-400"
            />
            <p className="text-xs text-slate-500 mt-1">
              The user will be notified when you save a response.
            </p>
          </div>

          {/* Status Update */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Update Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="RESOLVED">Resolved</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Admin Notes */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Admin Notes (Internal Only)</label>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add internal notes about this ticket (not visible to user)..."
              rows={3}
            />
          </div>

          {/* Resolution Info */}
          {ticket.resolved_by && (
            <div className="p-3 bg-lime-50 rounded-lg border border-lime-200">
              <p className="text-sm text-lime-700">
                <CheckCircle className="h-4 w-4 inline mr-1" />
                Resolved by {ticket.resolved_by} on {format(new Date(ticket.resolved_at), 'PPp')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-evergreen-950 hover:bg-evergreen-900"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save & Notify User'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminSupportTicketsPage() {
  const { authAxios, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await authAxios.get(`/admin/support/tickets?${params}`);
      setTickets(response.data.tickets || []);
      setStats(response.data.stats || { open: 0, in_progress: 0, resolved: 0 });
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, [authAxios, statusFilter, categoryFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await authAxios.put(`/admin/support/tickets/${ticketId}`, updates);
      toast.success('Ticket updated');
      setDetailOpen(false);
      loadTickets();
    } catch (error) {
      console.error('Failed to update ticket:', error);
      toast.error('Failed to update ticket');
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setDetailOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-evergreen-950 text-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <Link to="/admin" className="flex items-center gap-2">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10 brightness-0 invert" />
                <Badge variant="outline" className="border-white/30 text-white text-xs">Admin</Badge>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/guide')}
                className="text-white hover:bg-white/10"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Guide
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading text-slate-900 mb-1">Support Tickets</h1>
            <p className="text-slate-500">Manage user support requests</p>
          </div>
          <Button variant="outline" onClick={loadTickets} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Open</p>
                  <p className="text-3xl font-bold text-amber-700">{stats.open}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">In Progress</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.in_progress}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-lime-200 bg-lime-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-lime-600 font-medium">Resolved</p>
                  <p className="text-3xl font-bold text-lime-700">{stats.resolved}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-lime-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-500">Filter:</span>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="OPEN">Open</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="RESOLVED">Resolved</SelectItem>
              <SelectItem value="CLOSED">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="account">Account</SelectItem>
              <SelectItem value="matching">Matching</SelectItem>
              <SelectItem value="bug">Bug Report</SelectItem>
              <SelectItem value="feedback">Feature Request</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
          </div>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No tickets found</h3>
              <p className="text-slate-500">
                {statusFilter !== 'all' || categoryFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Support tickets will appear here when users submit them'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                onClick={() => handleViewTicket(ticket)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={handleUpdateTicket}
      />
    </div>
  );
}
